import { type SdkPermissionMode, ZSdkPermissionMode } from "./sdk-permission-mode.types";

/**
 * HTTP MCP server the in-sandbox agent SDK mounts to call host-owned tools.
 * `serverUrl` must resolve to the same backend host as `/api/mcp` so it stays
 * inside Daytona's egress allowlist; `authToken` is a scoped bearer JWT.
 */
export interface McpClientConfig {
  readonly serverUrl: string;
  readonly authToken: string;
}

/**
 * The built-in tool set a surface hands the SDK — either the names it wants, or
 * the preset standing for every built-in the runtime ships.
 *
 * The two differ in what happens when the runtime gains a tool. A surface that
 * names its tools keeps exactly those and must be edited to gain another; one
 * that names the preset gains each new built-in as the runtime gains it, and
 * narrows through `disallowedTools` instead. Neither is safer in the abstract:
 * a list cannot be surprised, a preset cannot go stale.
 */
export type SdkToolSet = string[] | { readonly type: "preset"; readonly preset: "claude_code" };

/**
 * Generic surface context fields understood by the middleware chain.
 * Any surface (Cue, future) can pass these.
 */
export interface GenericSurfaceContext {
  readonly conversationId?: string;
  readonly agentId?: string;
  readonly skipTracing?: boolean;
  /**
   * Opt-in key for the disconnect-survives-abort behavior in Anthropic/OpenAI
   * provider adapters. When present, a CLIENT_DISCONNECT abort routes the
   * remaining upstream events to the shared ResumableStreamBuffer keyed by
   * this string instead of cancelling the upstream HTTP call. A subsequent
   * /resume-stream request can drain the buffer and continue live.
   *
   * Convention: `${organizationId}:${conversationId-or-chatId}`. Surfaces
   * that don't support resume should leave this undefined — adapters fall
   * back to the existing kill-on-abort behavior.
   */
  readonly resumeStreamKey?: string;
}

/**
 * Daytona-specific surface context. Extends generic with sandbox fields.
 * Use `parseDaytonaSurfaceContext()` at adapter entry for validation.
 */
export interface DaytonaSurfaceContext extends GenericSurfaceContext {
  readonly sandboxKey: string;
  readonly resumeSessionId?: string;
  readonly afterCreateCommands?: readonly string[];
  /** Permission mode for the Claude Agent SDK. Optional; when omitted the SDK default applies. */
  readonly permissionMode?: SdkPermissionMode;
  /**
   * Claude Agent SDK tool-control surface — names match the SDK reference
   * (`@anthropic-ai/claude-agent-sdk` query options):
   *   - tools           → the built-in tool set made available to the agent. A
   *                       tool absent from it is unreachable rather than refused,
   *                       so no permission layer ever sees it.
   *   - allowedTools    → auto-approved without a permission prompt. Grants
   *                       nothing: it only spares a tool its prompt, so listing
   *                       an unpublished tool here changes nothing at all.
   *   - disallowedTools → removed from the agent's context entirely, and
   *                       evaluated ahead of both the PreToolUse hook and the
   *                       permission mode. More than one layer contributes: the
   *                       Daytona service unions per-verdict denies onto whatever
   *                       the surface set, so a surface writes its own and never
   *                       assumes it is the only writer.
   * Each is omitted (never `[]`) when the surface doesn't set it.
   */
  readonly tools?: SdkToolSet;
  readonly allowedTools?: string[];
  readonly disallowedTools?: string[];
  /** Organization id of the calling user. Drives shared-workspace volume subpath. */
  readonly organizationId?: string;
  /**
   * Daytona snapshot to create the sandbox from. When undefined, the service
   * falls back to the DAYTONA_SNAPSHOT_NAME env var. Lets surfaces with their
   * own pre-baked image (e.g. Cue) request a dedicated snapshot while
   * sharing the rest of the Daytona streaming pipeline.
   */
  readonly snapshotName?: string;
  /**
   * Workspace mounts the agent has access to with their access mode. The
   * Daytona provider builds bubblewrap `--ro-bind` / `--tmpfs` flags from
   * this to enforce per-mount RO or full revocation at the kernel mount-
   * namespace layer (the only place these can be enforced — Daytona's
   * VolumeMount API has no RO flag and can't unmount on a running sandbox).
   *
   * Modes:
   *   - READ        → `--ro-bind-try` (path stays visible, writes return EROFS)
   *   - READ_WRITE  → no override (base bind exposes it RW)
   *   - HIDDEN      → `--tmpfs` + `--remount-ro` (path appears empty + RO)
   *                   Used for mid-session revocation when the underlying
   *                   Daytona mount can't be removed but admin intent is to
   *                   take access away.
   *
   * Empty/undefined = no wrap.
   */
  readonly workspaceMounts?: readonly {
    readonly mountPath: string;
    readonly mode: "READ" | "READ_WRITE" | "HIDDEN";
  }[];
  /**
   * HTTP MCP server the runner mounts so the agent SDK can call host-owned
   * inline tools (e.g. Cue's modify_prompt). Undefined = no extra MCP
   * server mounted.
   */
  readonly mcpConfig?: McpClientConfig;
  /**
   * Manual-mode deferred-tool continuation. Set on the resume turn after the
   * host collected user verdicts for tool calls the PreToolUse hook deferred
   * in the prior turn. Mirrors `AgentQueryInput.toolContinuation`.
   */
  readonly toolContinuation?: {
    readonly verdicts: ReadonlyArray<{
      /** Original deferred toolCallId — lets the runner link the re-issued call back to it. */
      readonly toolCallId?: string;
      readonly toolName: string;
      readonly input?: Record<string, unknown>;
      readonly decision: "allow" | "deny";
      readonly message?: string;
      readonly updatedInput?: Record<string, unknown>;
    }>;
  };
  /**
   * Settled AskUserQuestion answer/skip continuation(s), delivered to a still-live
   * long-lived runner as a `tool_response` stdin line instead of relaunching a fresh
   * session transport. `answers` is keyed by verbatim question text (the SDK's own question hold contract). A
   * decline never reaches here — it has no stream (`DeclineCueQuestionUseCase`
   * delivers it directly).
   */
  readonly auqAnswerContinuation?: ReadonlyArray<{
    readonly toolCallId: string;
    readonly answers: Record<string, string>;
    /** Optional free-text carried alongside the structured answers, when present. */
    readonly response?: string;
  }>;
  /**
   * Outcome of a pre-stream eager sandbox creation done by the caller. Cue warms
   * the sandbox before the stream — and thus before the trace — opens. When set,
   * the create-stream service records THESE metrics on the trace's CREATE event
   * instead of its own warm re-resolve, so a cold start surfaces its real
   * api-create / wait / setup cost rather than the cheap re-attach.
   * Omitted when the stream itself does the first (cold) getOrCreate.
   */
  readonly eagerSandboxCreate?: {
    readonly isNew: boolean;
    readonly apiCreateMs?: number;
    readonly waitStartedMs?: number;
    readonly setupMs?: number;
  };
}

/**
 * Canonical set of trace tags — single source of truth for both the types package
 * and the backend Zod schema (which derives its enum from this array).
 */
export const TRACE_TAGS = ["ai-task", "public-api"] as const;

export type TraceTag = (typeof TRACE_TAGS)[number];

/**
 * Canonical set of trace sources — single source of truth for both the types package
 * and the backend repository (which derives JSONB path filters from `contextField`).
 *
 * `key`          — the value sent as the `source` API filter param
 * `label`        — human-readable label for UI display
 * `contextField` — the JSONB key on the trace context to check for existence
 *
 * Note: `agentId` and `runId` are excluded — they enrich other sources, not sources themselves.
 */
export const TRACE_SOURCES = [
  { key: "chatId", label: "Chat", contextField: "chatId" },
  { key: "cue", label: "Cue", contextField: "cueConversationId" },
  { key: "workflow", label: "Workflow", contextField: "workflowId" },
  { key: "voice-relay", label: "Voice Relay", contextField: "voiceRelayCallSid" },
  { key: "agent-creation", label: "Agent Creation", contextField: "agentCreationThreadId" },
  { key: "prompt-assistant", label: "Prompt Assistant", contextField: "promptAssistantThreadId" },
  // ADDED, never a rename of the row above. Ai-task creation used to write its
  // thread id under `agentCreationThreadId`, so "Agent Creation" selected
  // ai-task traces and nothing else — the label was wrong for every row it
  // returned. New traces carry the correct key; historical rows keep the old one
  // and keep answering the old filter, which is why both rows exist.
  {
    key: "ai-task-creation",
    label: "AI Task Creation",
    contextField: "aiTaskCreationThreadId"
  }
] as const;

export type TraceSource = (typeof TRACE_SOURCES)[number]["key"];

/**
 * Flat tracing descriptor passed through the streaming middleware to LLMTracingService.
 * Callers build this at the source — the middleware has zero knowledge of specific surfaces.
 */
export interface StreamTraceDescriptor {
  readonly traceContext?: Readonly<Record<string, string | undefined>>;
  readonly generationContext?: Readonly<Record<string, string | undefined>>;
  readonly tags?: readonly TraceTag[];
  readonly triggeredByUserId?: string;
}

function parseStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const strings = value.filter((v): v is string => typeof v === "string");
  return strings.length > 0 ? strings : undefined;
}

function parseMcpConfig(value: unknown): McpClientConfig | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const { serverUrl, authToken } = value as Record<string, unknown>;
  return typeof serverUrl === "string" &&
    serverUrl.length > 0 &&
    typeof authToken === "string" &&
    authToken.length > 0
    ? { serverUrl, authToken }
    : undefined;
}

function isWorkspaceMount(
  value: unknown
): value is { readonly mountPath: string; readonly mode: "READ" | "READ_WRITE" | "HIDDEN" } {
  return (
    typeof value === "object" &&
    value !== null &&
    "mountPath" in value &&
    "mode" in value &&
    typeof value.mountPath === "string" &&
    (value.mode === "READ" || value.mode === "READ_WRITE" || value.mode === "HIDDEN")
  );
}

/**
 * A tool set arrives either as a list of names or as the preset object. Anything
 * else is treated as absent, the same way an unparseable list is — a surface that
 * sent something malformed gets the runner's default rather than a crash.
 */
function parseToolSet(raw: unknown): SdkToolSet | undefined {
  if (
    typeof raw === "object" &&
    raw !== null &&
    !Array.isArray(raw) &&
    (raw as { type?: unknown }).type === "preset" &&
    (raw as { preset?: unknown }).preset === "claude_code"
  ) {
    return { type: "preset", preset: "claude_code" };
  }
  return parseStringArray(raw);
}

export function parseDaytonaSurfaceContext(
  raw: Readonly<Record<string, unknown>> | undefined
): DaytonaSurfaceContext {
  const sandboxKey = raw?.["sandboxKey"];
  if (typeof sandboxKey !== "string" || sandboxKey.length === 0) {
    throw new Error(
      "[DaytonaStreamProviderAdapter] surfaceContext.sandboxKey is required (string). " +
        "Pass { sandboxKey: '<scope-id>' } in StreamProviderInput.surfaceContext."
    );
  }
  const rawAfterCreateCommands = raw?.["afterCreateCommands"];
  const afterCreateCommands =
    Array.isArray(rawAfterCreateCommands) && rawAfterCreateCommands.length > 0
      ? (rawAfterCreateCommands.filter(
          (c): c is string => typeof c === "string"
        ) as readonly string[])
      : undefined;

  // `tools` is the one field of the three that is not always a list: a surface
  // may hand over the preset instead, and dropping the object here would fall
  // the sandbox back to the default set with nothing reporting it.
  const tools = parseToolSet(raw?.["tools"]);
  const allowedTools = parseStringArray(raw?.["allowedTools"]);
  const disallowedTools = parseStringArray(raw?.["disallowedTools"]);

  const rawMounts = raw?.["workspaceMounts"];
  const parsedMounts = Array.isArray(rawMounts) ? rawMounts.filter(isWorkspaceMount) : [];
  const workspaceMounts = parsedMounts.length > 0 ? parsedMounts : undefined;

  const rawToolContinuation = raw?.["toolContinuation"];
  let toolContinuation: DaytonaSurfaceContext["toolContinuation"] | undefined = undefined;
  if (
    typeof rawToolContinuation === "object" &&
    rawToolContinuation !== null &&
    "verdicts" in rawToolContinuation &&
    Array.isArray((rawToolContinuation as Record<string, unknown>)["verdicts"])
  ) {
    const rawVerdicts = (rawToolContinuation as Record<string, unknown>)["verdicts"] as unknown[];
    const verdicts = rawVerdicts
      .filter(
        (v): v is Record<string, unknown> =>
          typeof v === "object" &&
          v !== null &&
          typeof (v as Record<string, unknown>)["toolName"] === "string" &&
          ((v as Record<string, unknown>)["decision"] === "allow" ||
            (v as Record<string, unknown>)["decision"] === "deny")
      )
      .map((v) => ({
        toolName: v["toolName"] as string,
        decision: v["decision"] as "allow" | "deny",
        ...(typeof v["toolCallId"] === "string" ? { toolCallId: v["toolCallId"] } : {}),
        ...(typeof v["message"] === "string" ? { message: v["message"] } : {}),
        ...(typeof v["input"] === "object" && v["input"] !== null
          ? { input: v["input"] as Record<string, unknown> }
          : {}),
        ...(typeof v["updatedInput"] === "object" && v["updatedInput"] !== null
          ? { updatedInput: v["updatedInput"] as Record<string, unknown> }
          : {})
      }));
    if (verdicts.length > 0) {
      toolContinuation = { verdicts };
    }
  }

  const rawQuestionHoldAnswerContinuation = raw?.["auqAnswerContinuation"];
  let questionHoldAnswerContinuation: DaytonaSurfaceContext["auqAnswerContinuation"] | undefined =
    undefined;
  if (Array.isArray(rawQuestionHoldAnswerContinuation)) {
    const parsed = rawQuestionHoldAnswerContinuation
      .filter(
        (v): v is Record<string, unknown> =>
          typeof v === "object" &&
          v !== null &&
          typeof (v as Record<string, unknown>)["toolCallId"] === "string" &&
          typeof (v as Record<string, unknown>)["answers"] === "object" &&
          (v as Record<string, unknown>)["answers"] !== null
      )
      .map((v) => ({
        toolCallId: v["toolCallId"] as string,
        answers: v["answers"] as Record<string, string>,
        ...(typeof v["response"] === "string" ? { response: v["response"] as string } : {})
      }));
    if (parsed.length > 0) {
      questionHoldAnswerContinuation = parsed;
    }
  }

  const rawEager = raw?.["eagerSandboxCreate"];
  const eagerSandboxCreate =
    typeof rawEager === "object" &&
    rawEager !== null &&
    typeof (rawEager as Record<string, unknown>)["isNew"] === "boolean"
      ? (() => {
          const e = rawEager as Record<string, unknown>;
          const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
          return {
            isNew: e["isNew"] as boolean,
            ...(num(e["apiCreateMs"]) !== undefined ? { apiCreateMs: num(e["apiCreateMs"]) } : {}),
            ...(num(e["waitStartedMs"]) !== undefined
              ? { waitStartedMs: num(e["waitStartedMs"]) }
              : {}),
            ...(num(e["setupMs"]) !== undefined ? { setupMs: num(e["setupMs"]) } : {})
          };
        })()
      : undefined;

  return {
    sandboxKey,
    resumeSessionId:
      typeof raw?.["resumeSessionId"] === "string" ? raw["resumeSessionId"] : undefined,
    conversationId: typeof raw?.["conversationId"] === "string" ? raw["conversationId"] : undefined,
    agentId: typeof raw?.["agentId"] === "string" ? raw["agentId"] : undefined,
    skipTracing: raw?.["skipTracing"] === true,
    afterCreateCommands:
      afterCreateCommands && afterCreateCommands.length > 0 ? afterCreateCommands : undefined,
    permissionMode: ZSdkPermissionMode.safeParse(raw?.["permissionMode"]).success
      ? (raw?.["permissionMode"] as SdkPermissionMode)
      : undefined,
    tools,
    allowedTools,
    disallowedTools,
    organizationId: typeof raw?.["organizationId"] === "string" ? raw["organizationId"] : undefined,
    snapshotName: typeof raw?.["snapshotName"] === "string" ? raw["snapshotName"] : undefined,
    workspaceMounts,
    mcpConfig: parseMcpConfig(raw?.["mcpConfig"]),
    ...(toolContinuation !== undefined ? { toolContinuation } : {}),
    ...(questionHoldAnswerContinuation !== undefined
      ? { auqAnswerContinuation: questionHoldAnswerContinuation }
      : {}),
    ...(eagerSandboxCreate !== undefined ? { eagerSandboxCreate } : {})
  };
}
